import type { FC, KeyboardEvent } from 'react';
import React, { useMemo, useState } from 'react';
import {
  Box,
  InputBase,
  MenuItem,
  MenuList,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Iconify } from '../../Iconify';
import type { SelectOption } from '../declarations/type';

// Short lists are faster to scan than to type into.
const SEARCH_MIN_OPTIONS = 7;

interface SelectMenuProps {
  options: SelectOption[];
  value: string | undefined;
  onChange: (id: string) => void;
}

/**
 * The options of a select filter as a plain menu, for the filter pill popover.
 * Long lists get a search field on top; Enter picks the first match.
 *
 * @example
 * <SelectMenu options={cashLists} value={cashListId} onChange={setCashListId} />
 */
export const SelectMenu: FC<SelectMenuProps> = ({
  options,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const searchable = options.length >= SEARCH_MIN_OPTIONS;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle
      ? options.filter((option) => option.label.toLowerCase().includes(needle))
      : options;
  }, [options, query]);

  const onSearchKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && matches[0]) {
      onChange(matches[0].id);
    }
  };

  return (
    <Box sx={{ width: 280 }}>
      {searchable && (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            height: 40,
            px: 1.75,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Iconify
            icon="eva:search-fill"
            width={18}
            sx={{ color: 'text.disabled' }}
          />
          <InputBase
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={t('table.filters.search')}
            sx={{ flex: 1, fontSize: 15, color: 'primary.main' }}
          />
        </Stack>
      )}
      <MenuList disablePadding sx={{ display: 'grid', gap: 0.25, p: 0.75 }}>
        {matches.map((option) => {
          const selected = option.id === value;
          return (
            <MenuItem
              key={option.id}
              selected={selected}
              onClick={() => onChange(option.id)}
              sx={{
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                fontSize: 15,
                color: 'primary.main',
                '&:hover': { bgcolor: 'background.neutral' },
                '&.Mui-selected, &.Mui-selected:hover': {
                  bgcolor: 'primary.500_8',
                },
              }}
            >
              <Box sx={{ width: 16, display: 'flex' }}>
                {selected && <Iconify icon="eva:checkmark-fill" width={16} />}
              </Box>
              {option.label}
            </MenuItem>
          );
        })}
      </MenuList>
      {matches.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2.25, pb: 2 }}
        >
          {t('table.filters.no-match')}
        </Typography>
      )}
    </Box>
  );
};
